import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { track } from '@amplitude/analytics-browser';
import { Virtuoso } from 'react-virtuoso';
import { GreenroomState } from '../../stores/types/types';
import * as ChatActions from '../../stores/actions/chat';
import type { ChatPost } from '../../stores/types/chat';
import { makeId } from '../../stores/reducers/chat';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import ChatLink from './chat-link';
import ChatMessage from './chat-message';
import { random } from '../../utils/random';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import usePrevious from '../../utils/use-previous';
import useScrolledToBottom from '../../hooks/use-scrolled-to-bottom';
import * as NotificationBuilder from '../../utils/notifications-builder';
import useChat from '../../hooks/use-chat';
import * as ChatApi from '../../connection/chat';
import { AmpEvent } from '../../types/amplitude';
import { TABS } from '../../stores/types/tabs';
import ChatInput from './chat-input';
import LoadingSpinner from '../general-components/loading-spinner';
import colors from '../../utils/colors';
import logger from '../../utils/logger-rum';

interface OwnProps {
	// props that will be passed to this component
	socket: Socket | null;
	inputText: string;
	setInputText: (text: string) => void;
}

export default function ChatConvoView(props: OwnProps) {
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const activeTab = useSelector((state: GreenroomState) => state.TabsReducer.activeTab);
	const directMessages = useSelector((state: GreenroomState) => state.ChatReducer.directMessages);
	const selectedUser = useSelector((state: GreenroomState) => state.ChatReducer.selectedUser);
	const [isFetchingMore, setIsFetchingMore] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(true);

	const { socket, inputText, setInputText } = props;

	const dispatch = useDispatch();
	const { setChatNotificationsSeen } = useChat();

	const container = useRef<HTMLDivElement | null>(null);

	const convo = user && selectedUser
		? directMessages[makeId([selectedUser?.email, user.email])]
		: null;

	const prevConvo = usePrevious(convo);
	const prevActiveTab = usePrevious(activeTab);
	const { scrolledToBottom } = useScrolledToBottom({ scrolledContainerRef: container?.current });
	const loadMoreMessages = useCallback(async () => {
		if (isFetchingMore || !token || !selectedUser || !user || !meeting?.meeting) return;
		setIsFetchingMore(true);

		if (convo && !!convo.messages.length) {
			dispatch(ChatActions.loadChatPosts(
				token,
				meeting.meeting,
				selectedUser.gr_user,
				convo.messages[convo.messages.length - 1].id,
			));
		}

		setIsFetchingMore(false);
	}, [convo, dispatch, isFetchingMore, meeting?.meeting, selectedUser, token, user]);

	async function sendMessage(mess?: string) {
		if (
			!mess
			|| !user
			|| !selectedUser
			|| !token
			|| !meeting
		) {
			return;
		}

		const message = {
			id: 0,
			recipient: {
				first_name: selectedUser.first_name,
				last_name: selectedUser.last_name,
				email: selectedUser.email,
				avatar: selectedUser.avatar,
				gr_user: selectedUser.gr_user,
			},
			sender: {
				first_name: user.first_name,
				last_name: user.last_name,
				email: user.email,
				avatar: user.avatar,
				gr_user: user.gr_user,
			},
			meeting: meeting.meeting,
			room: undefined,
			created: Date.now(),
			message: mess,
			seen: false,
			attachment: undefined,
			mentions: [],
			randomId: random(20),
		};

		message.message = message.message.replace(/\n/g, '\n\n');

		// Do not send blank messages
		if (!message.message.trim()) return;

		// send chat to API, make sure it's successful before sending it to other user
		try {
			track(AmpEvent.chatMessageSent);
			const res = await ChatApi.PostChat(
				token,
				meeting.meeting,
				mess,
				undefined,
				undefined,
				selectedUser.gr_user,
			);
			if (!res) {
				logger.error('unable to create direct chat message');
				return;
			}
			message.id = res.insertId;
		} catch (e) {
			logger.error(`unable to create direct chat message due to ${(e as Error).message}`);
			return;
		}

		// only send the socket message if the user's socket message is correct
		if (socket) {
			// If the receiving user reconnects/refreshes their socketId can
			// change so they wouldn't receive this next message if the sender
			// still had their DM tab open because selectedUser doesn't automatically update
			const receivingConnectedUser = connectedUsers
				.find((recievingUser: ConnectedUser) => recievingUser.gr_user === selectedUser.gr_user);
			if (receivingConnectedUser && receivingConnectedUser?.socketId !== selectedUser?.socketId) {
				dispatch(ChatActions.setSelectedUser(receivingConnectedUser));
				socket.sendChat(String(receivingConnectedUser.socketId), message);
			} else {
				socket.sendChat(String(selectedUser.socketId), message);
			}
		}

		if (token && selectedUser) {
			NotificationBuilder.commentBasicNotification(token, user, [selectedUser], meeting);
		}

		// add into UI
		dispatch(ChatActions.newMessage(message as any));
	}

	function goBack() {
		dispatch(ChatActions.setSelectedUser(null));
	}

	useEffect(() => {
		const authorId = selectedUser?.gr_user;
		const messages: number[] = convo?.messages?.reduce((acc: number[], cur?: ChatPost) => {
			if (cur?.id && (!cur.seen || cur.isNew)) {
				return [...acc, cur.id];
			}

			return acc;
		}, []) ?? [];
		if (
			token
			&& meeting?.meeting
			&& authorId !== undefined
			&& messages?.length
			&& activeTab === 'chat'
		) {
			dispatch(
				ChatActions.setDirectMessagesSeen(
					token,
					meeting?.meeting,
					authorId,
					messages,
				),
			);
		}
	}, [convo, meeting, selectedUser, token, activeTab]);

	useEffect(() => {
		// scroll to bottom of the chat window if the user:
		// - previously scrolled to the bottom and a new message is sent or received
		// - entered a new chat room
		// - changed tabs
		if (
			container.current
			&& ((prevConvo?.messages?.length !== convo?.messages?.length && scrolledToBottom)
			|| !prevConvo || prevActiveTab !== TABS.CHAT)
		) {
			container.current?.scrollTo({ top: container.current.scrollHeight, behavior: 'auto' });
		}
		setChatNotificationsSeen();
	}, [convo, activeTab]);

	useEffect(() => {
		if (!token || !selectedUser || !meeting?.meeting) return;

		// eslint-disable-next-line camelcase
		const { gr_user, email: selectedEmail } = selectedUser;
		const { email: userEmail } = user ?? {};

		const fetchData = async () => {
			setIsLoading(true);

			if (selectedEmail && userEmail) {
				const chatId = makeId([selectedEmail, userEmail]);
				dispatch(ChatActions.clearChatDirectChatId({ key: chatId, all: true }));
			}

			try {
				await dispatch(ChatActions.loadChatPosts(
					token,
					meeting.meeting,
					gr_user,
				));
			} catch (error: any) {
				logger.error(error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();

		// Cleanup
		// eslint-disable-next-line consistent-return
		return () => {
			if (selectedEmail && userEmail) {
				const chatId = makeId([selectedEmail, userEmail]);
				dispatch(ChatActions.clearChatDirectChatId({ key: chatId }));
			}
		};
	}, []);

	const orderedMessages = useMemo(() => {
		if (!convo || !convo.messages) return [];

		return [...convo.messages].reverse().map((msg, index, arr) => {
			const prevMsg = arr[index - 1];
			const isChild = prevMsg?.sender?.gr_user === msg?.sender?.gr_user;

			return {
				...msg,
				isChild,
			};
		});
	}, [convo]);

	return convo ? (
		<div className="room-wrapper display-flex flex-flow-column height-full">
			<div>
				<button className="chat-back-button transparent" onClick={goBack}><Icon name={ICONS.ARROW_LEFT} color={COLORS.DEFAULT_GRAY} size={17}></Icon> <span>All</span></button>
			</div>
			{isLoading
				? <div className="notes-loading-container">
					<LoadingSpinner
						height={100}
						width={100}
						color={colors.red}
					/>
				</div>
				: <>
					<div className='chat-top height-fit-content max-height-half padding-bottom-medium'>
						{!!selectedUser && (
							<ChatLink
								user={selectedUser}
								message={{ message: `${convo?.meta?.totalMessageCount} messages` }}
								inRoom={true}
								socket={socket}
							/>
						)}
					</div>

					<div className='chat-container height-full overflow-scroll-y' ref={container}>
						{<Virtuoso
							style={{ height: '100%' }}
							data={orderedMessages || []}
							firstItemIndex={orderedMessages[0]?.id ?? 0}
							initialTopMostItemIndex={orderedMessages.length - 1}
							increaseViewportBy={200}
							startReached={loadMoreMessages}
							alignToBottom
							followOutput="auto"
							itemContent={(index, message: ChatPost) => (
								<ChatMessage
									key={message.id ?? index + (message.randomId || '')}
									post={message}
									user={message.sender}
									isChild={message.isChild}
									socket={socket}
								/>
							)}
						/>}
					</div>
					{user && (
						<ChatInput
							user={user}
							onSendMessage={sendMessage}
							inputText={inputText}
							setInputText={setInputText}
						/>
					)}
				</>}
		</div>
	) : (
		<div className="room-wrapper">
			<button className="chat-back-button transparent" onClick={goBack}><Icon name={ICONS.ARROW_LEFT} color={COLORS.DEFAULT_GRAY} size={17}></Icon> <span>All</span></button>
		</div>
	);
}
